import React from 'react';
import { Alert } from 'antd';
import moment from 'moment';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import { PerformanceScatterChart, PerformerLegend } from '../analytics_charts';
import {
	COMPARISON_PREVIOUS_BORDER_COLOR,
	MAX_SUPPLIERS_IN_CHART,
	PERFORMER_GROUP_LABELS,
} from './SupplierPerformanceConstants';
import { PERFORMER_SEGMENT } from '../../utils/SupplierPerformerSegmentationUtils';

const PERFORMER_LEGEND_RESERVED_PX = 32;
const COMPARISON_LEGEND_RESERVED_PX = 32;
const OVERFLOW_ALERT_RESERVED_PX = 40;
const TOOLTIP_DOM_STYLES: Record<string, React.CSSProperties> = {
	'g2-tooltip': {
		padding: '8px',
		borderRadius: '2px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
		backgroundColor: '#ffffff',
	},
	'g2-tooltip-title': { margin: '0', display: 'none' },
	'g2-tooltip-list': { margin: '0', padding: '0' },
	'g2-tooltip-list-item': { margin: '0', padding: '0' },
};

const SupplierPerformanceScatterSection = ({
	chartData,
	totalChartRowCount,
	hasComparison,
	comparisonMode,
	edges,
	pointColor,
	pointShape,
	pointSize,
	pointStyle,
	pointLabel,
	pointTooltipDefinition,
	chartColor,
	formatDurationAxisLabel,
	handlePointClick,
	selectedRange,
	comparisonRange,
	scale,
	chartHeight = 586,
	onGetG2Instance,
}) => {
	const selectedStart: moment.Moment | null = selectedRange ? selectedRange[0] : null;
	const selectedEnd: moment.Moment | null = selectedRange ? selectedRange[1] : null;
	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const tooManyRows = totalChartRowCount > MAX_SUPPLIERS_IN_CHART;
	const innerChartHeight = Math.max(
		200,
		chartHeight -
			PERFORMER_LEGEND_RESERVED_PX -
			(hasComparison ? COMPARISON_LEGEND_RESERVED_PX : 0) -
			(tooManyRows ? OVERFLOW_ALERT_RESERVED_PX : 0)
	);

	if (chartData.length === 0) {
		return (
			<div
				className="flex items-center justify-center px-6 text-center"
				style={{ height: chartHeight }}
			>
				<span className="bodyregular text-character-secondary">
					No suppliers have both response and resolution time data for this period.
				</span>
			</div>
		);
	}

	return (
		<>
			{tooManyRows && (
				<Alert
					className="!mb-0 !rounded-none !border-none bg-character-dangerBg"
					type="error"
					banner
					showIcon={false}
					closable={false}
					message={`Too much data to display. showing ${MAX_SUPPLIERS_IN_CHART}/${totalChartRowCount} suppliers. Filter to narrow selection`}
				/>
			)}
			<div className="w-full min-w-0">
				<PerformanceScatterChart
					data={chartData}
					xField="avgResponseTimeMinutes"
					yField="avgResolutionMinutes"
					xAxisTitle="Avg. Response Time"
					yAxisTitle="Avg. Resolution Time"
					formatAxisLabel={formatDurationAxisLabel}
					pointColor={pointColor}
					pointShape={pointShape}
					pointSize={pointSize}
					pointStyle={pointStyle}
					pointLabel={pointLabel}
					edges={edges}
					height={innerChartHeight}
					onGetG2Instance={onGetG2Instance}
					pointTooltipDefinition={pointTooltipDefinition}
					tooltipDomStyles={TOOLTIP_DOM_STYLES}
					onPointClick={handlePointClick}
					scale={scale}
				/>
				<PerformerLegend
					color={chartColor}
					labels={{
						top: PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.TOP],
						typical: PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.TYPICAL],
						bottom: PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.BOTTOM],
					}}
				/>
				{hasComparison && (
					<ComparisonLegend
						currentStart={selectedStart}
						currentEnd={selectedEnd}
						previousStart={comparisonStart}
						previousEnd={comparisonEnd}
						currentColor={chartColor}
						previousColor={COMPARISON_PREVIOUS_BORDER_COLOR}
						mode={comparisonMode}
					/>
				)}
			</div>
		</>
	);
};

export default SupplierPerformanceScatterSection;
